/**
 * workload-picker.js — Site workload picker UI
 *
 * Owns the portal-based workload picker dropdown: state query/mutation,
 * markup builders, positioning, open/close, search/filter, and
 * category expansion.
 *
 * Extracted from the workload-picker section of script.js (was lines 460–973).
 *
 * Cross-dependency: openWorkloadPicker calls window.closeSiteScopePicker (set by
 * site-scope-picker.js). window bridge avoids circular-import init-order issues.
 */

import { workloadPickerUiState, siteScopePickerUiState, sites } from './state.js';
import { workloadOptionGroups, inDevelopmentWorkloadStatusLabel } from './workload-defs.js';
import {
    buildWorkloadPickerKey, parseWorkloadPickerKey, buildWorkloadPickerGroupId,
    getSiteWorkloadPickerTriggerSelector, getSiteWorkloadPickerSearchSelector,
    getWorkloadPickerGroupLabel, normalizeWorkloadPickerSearchQuery,
    clearWorkloadPickerPortal, getWorkloadPickerViewportBottomBoundary,
    getUnavailableSiteWorkloadValues, hasSelectedWorkloadValue,
    isInDevelopmentWorkloadValue, getSiteById,
} from './helpers.js';
import { getWorkloadRowId, getSiteWorkloadByRowId } from './palette.js';
import { escapeHtml } from './utils.js';
import { getSiteDisplayName, getWorkloadLabel } from './recalc.js';

// ── Portal host ────────────────────────────────────────────────────────────────

const workloadPickerPortalHost = document.createElement('div');
window.workloadPickerPortalHost = workloadPickerPortalHost;
workloadPickerPortalHost.className = 'workload-picker-portal-host';
workloadPickerPortalHost.hidden = true;
document.body?.appendChild(workloadPickerPortalHost);

// ── Module state ───────────────────────────────────────────────────────────────

export const workloadPickerPlaceholderLabel = 'Select Workload...';
let workloadPickerRepositionFrame = 0;
window.workloadPickerRepositionFrame = workloadPickerRepositionFrame;

// ── Private helpers ────────────────────────────────────────────────────────────

function getDefaultWorkloadPickerExpandedLabels(workloadValue = '') {
    const selectedGroupLabel = getWorkloadPickerGroupLabel(workloadValue);
    if (selectedGroupLabel) {
        return [selectedGroupLabel];
    }

    return [];
}

function getWorkloadPickerSearchQuery(siteId, workloadRowId) {
    return workloadPickerUiState.searchQueryByKey[buildWorkloadPickerKey(siteId, workloadRowId)] || '';
}

function getWorkloadPickerExpandedLabels(siteId, workloadRowId, workloadValue = '') {
    const key = buildWorkloadPickerKey(siteId, workloadRowId);
    if (Object.hasOwn(workloadPickerUiState.expandedLabelsByKey, key)) {
        const expandedLabels = workloadPickerUiState.expandedLabelsByKey[key];
        return Array.isArray(expandedLabels) ? expandedLabels : [];
    }

    return getDefaultWorkloadPickerExpandedLabels(workloadValue);
}

export function setWorkloadPickerSearchQuery(siteId, workloadRowId, searchQuery) {
    workloadPickerUiState.searchQueryByKey[buildWorkloadPickerKey(siteId, workloadRowId)] = String(searchQuery || '');
}

function setWorkloadPickerExpandedLabels(siteId, workloadRowId, workloadValue, labels) {
    const allowedLabels = new Set(workloadOptionGroups.map((group) => group.label));
    const nextLabels = Array.isArray(labels)
        ? labels.filter((label, index) => allowedLabels.has(label) && labels.indexOf(label) === index)
        : [];

    workloadPickerUiState.expandedLabelsByKey[buildWorkloadPickerKey(siteId, workloadRowId)] = nextLabels;
}

export function isWorkloadPickerOpen(siteId, workloadRowId) {
    return workloadPickerUiState.openKey === buildWorkloadPickerKey(siteId, workloadRowId);
}

function getWorkloadPickerDescriptor(siteId, workloadRowId) {
    const site = getSiteById(siteId);
    const workload = getSiteWorkloadByRowId(site, workloadRowId);
    if (!site || !workload) {
        return null;
    }

    const siteDisplayName = getSiteDisplayName(site, sites.findIndex((item) => item.id === site.id));
    const workloadLabel = getWorkloadLabel(workload.value);
    const pickerId = `site-workload-picker-${site.id}-${workloadRowId}`;

    return {
        site,
        workload,
        workloadRowId,
        pickerId,
        panelId: `${pickerId}-panel`,
        searchInputId: `${pickerId}-search`,
        siteDisplayName,
        workloadLabel,
        key: buildWorkloadPickerKey(site.id, workloadRowId)
    };
}

function getOpenWorkloadPickerDescriptor() {
    const parsedKey = parseWorkloadPickerKey(workloadPickerUiState.openKey);
    return parsedKey ? getWorkloadPickerDescriptor(parsedKey.siteId, parsedKey.workloadRowId) : null;
}

function getRenderedSiteWorkloadPickerGroups(site, selectedValue = '') {
    const unavailableValues = getUnavailableSiteWorkloadValues(site, selectedValue);

    return workloadOptionGroups.map((group) => ({
        label: group.label,
        options: group.options.filter((option) => option.value === selectedValue || !unavailableValues.has(option.value))
    }));
}

function buildSiteWorkloadPickerPanelMarkup(descriptor) {
    const { site, workload, workloadRowId, pickerId, panelId, searchInputId, siteDisplayName, workloadLabel, key } = descriptor;
    const isOpen = isWorkloadPickerOpen(site.id, workloadRowId);
    const searchQuery = getWorkloadPickerSearchQuery(site.id, workloadRowId);
    const expandedLabels = new Set(getWorkloadPickerExpandedLabels(site.id, workloadRowId, workload.value));
    const pickerGroups = getRenderedSiteWorkloadPickerGroups(site, workload.value);
    const hasOptions = pickerGroups.some((group) => group.options.length);

    const categoriesMarkup = pickerGroups.map((group) => {
        const groupId = buildWorkloadPickerGroupId(pickerId, group.label);
        const isExpanded = isOpen && expandedLabels.has(group.label);
        const optionsMarkup = group.options.map((option) => {
            const isSelected = option.value === workload.value;
            const isInDevelopment = isInDevelopmentWorkloadValue(option.value);
            const optionMetaMarkup = [
                isInDevelopment
                    ? `<span class="site-workload-picker__option-tag">${escapeHtml(inDevelopmentWorkloadStatusLabel)}</span>`
                    : '',
                isSelected ? '<span class="site-workload-picker__option-badge">Selected</span>' : ''
            ].filter(Boolean).join('');

            return `
                <button
                    type="button"
                    class="site-workload-picker__option${isSelected ? ' is-selected' : ''}${isInDevelopment ? ' workload-disabled' : ''}"
                    data-site-id="${site.id}"
                    data-site-workload-picker-row="${workloadRowId}"
                    data-site-workload-picker-next-value="${escapeHtml(option.value)}"
                    data-workload-picker-option-label="${escapeHtml(option.label.toLowerCase())}"
                    aria-pressed="${String(isSelected)}"
                    aria-disabled="${String(isInDevelopment)}"
                    ${isInDevelopment ? 'disabled' : ''}
                >
                    <span class="site-workload-picker__option-label">${escapeHtml(option.label)}</span>
                    ${optionMetaMarkup ? `<span class="site-workload-picker__option-meta">${optionMetaMarkup}</span>` : ''}
                </button>
            `;
        }).join('');

        return `
            <section
                class="site-workload-picker__category"
                data-workload-picker-category-shell="${escapeHtml(group.label)}"
                ${group.options.length ? '' : 'hidden'}
            >
                <div role="heading" aria-level="4" class="site-workload-picker__category-heading">
                    <button
                        type="button"
                        class="site-workload-picker__category-toggle"
                        data-site-id="${site.id}"
                        data-site-workload-picker-category-row="${workloadRowId}"
                        data-workload-picker-category-label="${escapeHtml(group.label)}"
                        aria-expanded="${String(isExpanded)}"
                        aria-controls="${groupId}"
                    >
                        <span class="site-workload-picker__category-label">${escapeHtml(group.label)}</span>
                        <span class="site-workload-picker__category-meta">
                            <span class="site-workload-picker__category-count" data-workload-picker-category-count>${group.options.length}</span>
                            <span class="site-workload-picker__category-chevron" aria-hidden="true">⌄</span>
                        </span>
                    </button>
                </div>
                <div id="${groupId}" class="site-workload-picker__category-panel" ${isExpanded ? '' : 'hidden'}>
                    ${optionsMarkup}
                </div>
            </section>
        `;
    }).join('');

    return `
        <div
            id="${panelId}"
            class="site-workload-picker__panel site-workload-picker__panel--portal"
            data-site-id="${site.id}"
            data-site-workload-picker-panel="${workloadRowId}"
            data-workload-picker-key="${escapeHtml(key)}"
            aria-labelledby="${pickerId}"
            ${isOpen ? '' : 'hidden'}
        >
            <div class="site-workload-picker__search-shell">
                <label class="sr-only" for="${searchInputId}">Search</label>
                <div class="site-workload-picker__search-control">
                    <span class="site-workload-picker__search-icon" aria-hidden="true">
                        <svg viewBox="0 0 16 16" focusable="false">
                            <circle cx="7" cy="7" r="4.5"></circle>
                            <path d="M10.5 10.5 14 14"></path>
                        </svg>
                    </span>
                    <input
                        type="search"
                        id="${searchInputId}"
                        class="site-workload-picker__search"
                        value="${escapeHtml(searchQuery)}"
                        placeholder="Search"
                        autocomplete="off"
                        spellcheck="false"
                        data-site-id="${site.id}"
                        data-site-workload-picker-search="${workloadRowId}"
                    />
                </div>
            </div>
            <div class="site-workload-picker__categories" data-site-workload-picker-categories="${workloadRowId}">
                ${categoriesMarkup}
                <p class="site-workload-picker__empty" data-site-workload-picker-empty="${workloadRowId}" ${hasOptions ? 'hidden' : ''}>No results found</p>
            </div>
        </div>
    `;
}

// ── Exports ────────────────────────────────────────────────────────────────────

export function buildSiteWorkloadPickerMarkup(site, workload, pickerId, siteDisplayName, workloadLabel) {
    const workloadRowId = getWorkloadRowId(workload);
    const isOpen = isWorkloadPickerOpen(site.id, workloadRowId);
    const panelId = `${pickerId}-panel`;
    const hasSelection = hasSelectedWorkloadValue(workload.value);
    const triggerLabel = hasSelection ? workloadLabel : workloadPickerPlaceholderLabel;

    return `
        <div
            class="site-workload-picker${isOpen ? ' is-open' : ''}"
            data-site-id="${site.id}"
            data-site-workload-picker-root="${workloadRowId}"
            data-workload-picker-key="${escapeHtml(buildWorkloadPickerKey(site.id, workloadRowId))}"
        >
            <button
                type="button"
                id="${pickerId}"
                class="site-workload-picker__trigger"
                data-site-id="${site.id}"
                data-site-workload-picker-trigger="${workloadRowId}"
                aria-expanded="${String(isOpen)}"
                aria-controls="${panelId}"
                aria-haspopup="listbox"
            >
                <span class="site-workload-picker__trigger-copy">
                    <span class="site-workload-picker__trigger-label${hasSelection ? '' : ' site-workload-picker__trigger-label--placeholder'}">${escapeHtml(triggerLabel)}</span>
                </span>
                <span class="site-workload-picker__trigger-icon" aria-hidden="true">⌄</span>
            </button>
        </div>
    `;
}

function syncWorkloadPickerRootState(root) {
    if (!(root instanceof HTMLElement)) {
        return;
    }

    const siteId = parseInt(root.dataset.siteId, 10);
    const workloadRowId = parseInt(root.dataset.siteWorkloadPickerRoot, 10);
    if (!siteId || !workloadRowId) {
        return;
    }

    const isOpen = isWorkloadPickerOpen(siteId, workloadRowId);
    const trigger = root.querySelector(`[data-site-workload-picker-trigger="${workloadRowId}"]`);

    root.classList.toggle('is-open', isOpen);

    if (trigger instanceof HTMLElement) {
        trigger.setAttribute('aria-expanded', String(isOpen));
    }
}

export function syncWorkloadPickerPanelState(container, siteId, workloadRowId) {
    if (!container || typeof container.querySelector !== 'function') {
        return;
    }

    const descriptor = getWorkloadPickerDescriptor(siteId, workloadRowId);
    if (!descriptor) {
        return;
    }

    const panel = container.querySelector(`[data-workload-picker-key="${escapeHtml(buildWorkloadPickerKey(siteId, workloadRowId))}"]`);
    if (!(panel instanceof HTMLElement)) {
        return;
    }

    const isOpen = isWorkloadPickerOpen(siteId, workloadRowId);
    const query = getWorkloadPickerSearchQuery(siteId, workloadRowId);
    const normalizedQuery = normalizeWorkloadPickerSearchQuery(query);
    const expandedLabels = new Set(getWorkloadPickerExpandedLabels(siteId, workloadRowId, descriptor.workload.value));
    const searchInput = panel.querySelector(`[data-site-workload-picker-search="${workloadRowId}"]`);
    let visibleOptionTotal = 0;

    panel.hidden = !isOpen;

    if (searchInput instanceof HTMLInputElement && searchInput.value !== query) {
        searchInput.value = query;
    }

    panel.querySelectorAll('[data-workload-picker-category-shell]').forEach((category) => {
        if (!(category instanceof HTMLElement)) {
            return;
        }

        const groupLabel = category.dataset.workloadPickerCategoryShell || '';
        const categoryPanel = category.querySelector('.site-workload-picker__category-panel');
        const categoryToggle = category.querySelector('[data-site-workload-picker-category-row]');
        const categoryCount = category.querySelector('[data-workload-picker-category-count]');
        let visibleCount = 0;

        category.querySelectorAll('[data-site-workload-picker-next-value]').forEach((optionButton) => {
            if (!(optionButton instanceof HTMLElement)) {
                return;
            }

            const optionLabel = optionButton.dataset.workloadPickerOptionLabel || '';
            const matchesQuery = !normalizedQuery || optionLabel.includes(normalizedQuery);
            optionButton.hidden = !matchesQuery;

            if (matchesQuery) {
                visibleCount += 1;
            }
        });

        const shouldShowCategory = visibleCount > 0;
        const shouldExpandCategory = isOpen && (normalizedQuery ? shouldShowCategory : expandedLabels.has(groupLabel));

        category.hidden = !shouldShowCategory;

        if (categoryToggle instanceof HTMLElement) {
            categoryToggle.setAttribute('aria-expanded', String(shouldExpandCategory && shouldShowCategory));
        }

        if (categoryCount instanceof HTMLElement) {
            categoryCount.textContent = String(visibleCount);
        }

        if (categoryPanel instanceof HTMLElement) {
            categoryPanel.hidden = !shouldExpandCategory || !shouldShowCategory;
        }

        visibleOptionTotal += visibleCount;
    });

    const emptyState = panel.querySelector(`[data-site-workload-picker-empty="${workloadRowId}"]`);
    if (emptyState instanceof HTMLElement) {
        emptyState.hidden = visibleOptionTotal > 0;
    }
}

export function positionWorkloadPickerPortalPanel(panel, trigger) {
    if (!(panel instanceof HTMLElement) || !(trigger instanceof HTMLElement)) {
        return;
    }

    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    const horizontalPadding = 16;
    const verticalPadding = 16;
    const triggerRect = trigger.getBoundingClientRect();
    const bottomBoundary = getWorkloadPickerViewportBottomBoundary(viewportHeight, verticalPadding);
    const maxWidth = Math.max(280, viewportWidth - (horizontalPadding * 2));
    const width = Math.min(460, Math.max(350, Math.ceil(triggerRect.width)), maxWidth);
    const preferredBelowTop = Math.round(triggerRect.bottom + 8);
    const availableBelow = Math.max(0, bottomBoundary - preferredBelowTop);
    const availableAbove = Math.max(0, Math.round(triggerRect.top - verticalPadding - 8));

    panel.style.width = `${width}px`;
    panel.style.minWidth = `${Math.min(350, maxWidth)}px`;

    const naturalHeight = Math.min(360, Math.ceil(panel.scrollHeight || panel.offsetHeight || 360));
    const shouldOpenAbove = naturalHeight > availableBelow && availableAbove > availableBelow;
    const availableHeight = Math.max(160, Math.min(360, shouldOpenAbove ? availableAbove : availableBelow));
    const panelMaxHeight = Number.isFinite(availableHeight) ? availableHeight : 360;

    panel.style.maxHeight = `${panelMaxHeight}px`;

    const panelHeight = panel.offsetHeight;
    let left = Math.round(Math.min(Math.max(horizontalPadding, triggerRect.left), viewportWidth - width - horizontalPadding));
    let top = shouldOpenAbove
        ? Math.round(triggerRect.top - panelHeight - 8)
        : preferredBelowTop;

    top = Math.max(verticalPadding, Math.min(top, bottomBoundary - panelHeight));

    if (left + width > viewportWidth - horizontalPadding) {
        left = Math.max(horizontalPadding, viewportWidth - width - horizontalPadding);
    }

    panel.style.left = `${left}px`;
    panel.style.top = `${top}px`;
}

function positionOpenWorkloadPickerPanel() {
    const descriptor = getOpenWorkloadPickerDescriptor();
    if (!descriptor) {
        return;
    }

    const siteList = document.getElementById('site-list');
    const trigger = siteList.querySelector(getSiteWorkloadPickerTriggerSelector(descriptor.site.id, descriptor.workloadRowId));
    const panel = workloadPickerPortalHost.querySelector(`[data-workload-picker-key="${escapeHtml(descriptor.key)}"]`);

    if (!(trigger instanceof HTMLElement) || !(panel instanceof HTMLElement)) {
        return;
    }

    positionWorkloadPickerPortalPanel(panel, trigger);
}

export function scheduleOpenWorkloadPickerPanelPosition() {
    if (!workloadPickerUiState.openKey || workloadPickerRepositionFrame) {
        return;
    }

    workloadPickerRepositionFrame = window.requestAnimationFrame(() => {
        workloadPickerRepositionFrame = 0;
        window.workloadPickerRepositionFrame = workloadPickerRepositionFrame;
        positionOpenWorkloadPickerPanel();
    });
    window.workloadPickerRepositionFrame = workloadPickerRepositionFrame;
}

function renderWorkloadPickerPortal() {
    const descriptor = getOpenWorkloadPickerDescriptor();
    if (!descriptor) {
        clearWorkloadPickerPortal();
        return;
    }

    const siteList = document.getElementById('site-list');
    const trigger = siteList.querySelector(getSiteWorkloadPickerTriggerSelector(descriptor.site.id, descriptor.workloadRowId));
    if (!(trigger instanceof HTMLElement)) {
        clearWorkloadPickerPortal();
        return;
    }

    workloadPickerPortalHost.hidden = false;
    workloadPickerPortalHost.innerHTML = buildSiteWorkloadPickerPanelMarkup(descriptor);

    const panel = workloadPickerPortalHost.querySelector(`[data-workload-picker-key="${escapeHtml(descriptor.key)}"]`);
    if (!(panel instanceof HTMLElement)) {
        clearWorkloadPickerPortal();
        return;
    }

    syncWorkloadPickerPanelState(workloadPickerPortalHost, descriptor.site.id, descriptor.workloadRowId);
    positionWorkloadPickerPortalPanel(panel, trigger);
}

export function syncAllWorkloadPickerStates() {
    const siteList = document.getElementById('site-list');
    siteList.querySelectorAll('[data-site-workload-picker-root]').forEach((root) => {
        syncWorkloadPickerRootState(root);
    });

    renderWorkloadPickerPortal();
}

export function openWorkloadPicker(siteId, workloadRowId) {
    const descriptor = getWorkloadPickerDescriptor(siteId, workloadRowId);
    if (!descriptor) {
        return;
    }

    // Close the site scope picker if open — use window bridge to avoid
    // circular import init-order issues (site-scope-picker.js sets this).
    if (siteScopePickerUiState.openKey) {
        window.closeSiteScopePicker?.();
    }

    const key = buildWorkloadPickerKey(siteId, workloadRowId);

    workloadPickerUiState.openKey = key;

    if (!Object.hasOwn(workloadPickerUiState.expandedLabelsByKey, key)) {
        setWorkloadPickerExpandedLabels(siteId, workloadRowId, descriptor.workload.value, getDefaultWorkloadPickerExpandedLabels(descriptor.workload.value));
    }

    syncAllWorkloadPickerStates();

    const searchInput = workloadPickerPortalHost.querySelector(getSiteWorkloadPickerSearchSelector(siteId, workloadRowId));
    if (searchInput instanceof HTMLInputElement) {
        searchInput.focus();
        searchInput.select();
    }
}

export function closeWorkloadPicker(siteId = null, workloadRowId = 0) {
    const targetKey = siteId === null ? '' : buildWorkloadPickerKey(siteId, workloadRowId);

    if (siteId === null || workloadPickerUiState.openKey === targetKey) {
        workloadPickerUiState.openKey = '';
    }

    syncAllWorkloadPickerStates();
}

// window bridge so site-scope-picker.js can call this without a circular import
window.closeWorkloadPicker = closeWorkloadPicker;

export function toggleWorkloadPickerCategoryExpansion(siteId, workloadRowId, workloadValue, groupLabel) {
    if (normalizeWorkloadPickerSearchQuery(getWorkloadPickerSearchQuery(siteId, workloadRowId))) {
        return;
    }

    const expandedLabels = new Set(getWorkloadPickerExpandedLabels(siteId, workloadRowId, workloadValue));
    if (expandedLabels.has(groupLabel)) {
        expandedLabels.delete(groupLabel);
    } else {
        expandedLabels.add(groupLabel);
    }

    setWorkloadPickerExpandedLabels(siteId, workloadRowId, workloadValue, Array.from(expandedLabels));
}

export function resetWorkloadPickerState(siteId, workloadRowId) {
    const key = buildWorkloadPickerKey(siteId, workloadRowId);

    delete workloadPickerUiState.searchQueryByKey[key];
    delete workloadPickerUiState.expandedLabelsByKey[key];

    if (workloadPickerUiState.openKey === key) {
        workloadPickerUiState.openKey = '';
    }
}

export function pruneWorkloadPickerUiState() {
    const validKeys = new Set(
        sites.flatMap((site) => site.workloads.map((workload) => buildWorkloadPickerKey(site.id, getWorkloadRowId(workload))))
    );

    Object.keys(workloadPickerUiState.searchQueryByKey).forEach((key) => {
        if (!validKeys.has(key)) {
            delete workloadPickerUiState.searchQueryByKey[key];
        }
    });

    Object.keys(workloadPickerUiState.expandedLabelsByKey).forEach((key) => {
        if (!validKeys.has(key)) {
            delete workloadPickerUiState.expandedLabelsByKey[key];
        }
    });

    if (workloadPickerUiState.openKey && !validKeys.has(workloadPickerUiState.openKey)) {
        workloadPickerUiState.openKey = '';
    }
}
